import {
  Flex,
  Box,
  Card,
  CardBody,
  Divider,
  Input,
  InputGroup,
  InputRightElement,
  Button,
  CloseButton,
  useOutsideClick,
} from '@chakra-ui/react';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';

const Alias = ({
  editMode,
  name,
  index,
  primaryNameIndex,
  setPrimaryNameIndex,
  setAllAliases,
}: {
  editMode: boolean;
  name: string;
  index: number;
  primaryNameIndex: number;
  setPrimaryNameIndex: React.Dispatch<React.SetStateAction<number>>;
  setAllAliases: React.Dispatch<
    React.SetStateAction<
      {
        key: string;
        value: string;
      }[]
    >
  >;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editMode && name.trim() === '') {
      inputRef.current?.focus();
    }
  }, [editMode, name]);

  return (
    <>
      {editMode ? (
        <InputGroup>
          <Input
            value={name}
            onChange={(e) => {
              const newValue = e.target.value;
              setAllAliases((prev) => {
                const updated = [...prev];
                updated[index] = { ...updated[index], value: newValue }; // preserve key
                return updated;
              });
            }}
            variant="outline"
            isInvalid={name.trim() === ''}
            fontSize="0.875rem"
            pr={index === primaryNameIndex ? '40px' : '170px'}
            placeholder="Enter name"
            ref={inputRef}
          />
          <InputRightElement width="fit-content" mr="12px">
            {index === primaryNameIndex ? (
              <Icon icon="check" color="green" />
            ) : (
              <Flex gap={1}>
                <Button
                  size="sm"
                  fontSize="sm"
                  fontWeight="medium"
                  colorScheme="v2-aurora"
                  variant="ghost"
                  onMouseDown={(e) => {
                    // Prevent input blur when clicking the button
                    e.preventDefault();
                    e.stopPropagation();
                    setPrimaryNameIndex(index);
                  }}
                  isDisabled={name.trim() === ''}
                >
                  Make Primary
                </Button>
                <IconButtonV2
                  variant="v2-ghost"
                  size="sm"
                  aria-label="Delete alias"
                  tooltipEnabled={false}
                  icon={<Icon icon="trash-can" />}
                  colorScheme="gray"
                  onClick={() => {
                    setAllAliases((prev) => {
                      const updatedAliases = [...prev];
                      updatedAliases.splice(index, 1);
                      if (index === primaryNameIndex) {
                        setPrimaryNameIndex(0);
                      } else if (index < primaryNameIndex) {
                        setPrimaryNameIndex((prevIndex) => prevIndex - 1);
                      }
                      return updatedAliases;
                    });
                  }}
                />
              </Flex>
            )}
          </InputRightElement>
        </InputGroup>
      ) : (
        <Flex justifyContent="space-between" alignItems="center" gap={4}>
          <Typography variant="detail-muted">{name}</Typography>
          {index === primaryNameIndex && <Icon icon="check" color="green" />}
        </Flex>
      )}
    </>
  );
};

export const LimitedPartnerAliases = ({
  initialPrimaryName,
  initialAliases,
  updateNames,
}: {
  initialPrimaryName: string;
  initialAliases: string[];
  updateNames: (primaryName: string, aliases: string[]) => void;
}) => {
  const [editMode, setEditMode] = useState(false);
  const [primaryNameIndex, setPrimaryNameIndex] = useState(0);
  const [allAliases, setAllAliases] = useState(() => {
    const aliasSet = new Set(initialAliases);
    aliasSet.delete(initialPrimaryName);
    return [initialPrimaryName, ...aliasSet].map((alias) => {
      return { key: uuidv4(), value: alias.trim() };
    });
  });

  const reset = useCallback(() => {
    setPrimaryNameIndex(0);
    const aliasSet = new Set(initialAliases);
    aliasSet.delete(initialPrimaryName);
    setAllAliases(
      [initialPrimaryName, ...aliasSet].map((alias) => {
        return { key: uuidv4(), value: alias.trim() };
      }),
    );
  }, [initialPrimaryName, initialAliases]);

  useEffect(() => {
    reset();
  }, [initialPrimaryName, initialAliases, reset]);

  const boxRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: boxRef,
    handler: () => {
      setEditMode(false);
      reset();
    },
  });

  return (
    <Box role="group" width="100%" ref={boxRef}>
      <Card variant="outline" size="md" borderRadius="0.5rem">
        <CardBody display="flex" flexDirection="column" gap="0.5rem">
          <Flex justifyContent="space-between" alignItems="center">
            <Typography variant="detail" fontWeight="medium">
              Names
            </Typography>
            {editMode ? (
              <Flex>
                <CloseButton
                  onClick={() => {
                    setEditMode(false);
                    reset();
                  }}
                  aria-label="Cancel editing aliases"
                  colorScheme="gray"
                />
                <IconButtonV2
                  variant="v2-ghost"
                  size="sm"
                  aria-label="Save aliases"
                  tooltipEnabled={false}
                  icon={<Icon icon="save" />}
                  colorScheme="blue"
                  onClick={() => {
                    const primaryName = allAliases[
                      primaryNameIndex
                    ].value.trim();
                    const aliasSet = new Set(
                      allAliases.map((alias) => alias.value.trim()),
                    );
                    aliasSet.delete(primaryName); // Ensure primary name is not in aliases
                    updateNames(allAliases[primaryNameIndex].value, [
                      ...aliasSet,
                    ]);
                    setEditMode(false);
                  }}
                  isDisabled={allAliases.some(
                    (alias) => alias.value.trim() === '',
                  )}
                />
              </Flex>
            ) : (
              <Box
                opacity={0}
                transition="opacity 0.2s"
                _groupHover={{ opacity: 1 }}
              >
                <IconButtonV2
                  variant="v2-ghost"
                  size="sm"
                  aria-label="Edit aliases"
                  tooltipEnabled={false}
                  icon={<Icon icon="edit" />}
                  colorScheme="gray"
                  onClick={() => {
                    setEditMode(true);
                  }}
                />
              </Box>
            )}
          </Flex>
          <Divider />
          <Flex
            maxH="250px"
            overflowY="auto"
            gap="0.5rem"
            direction="column"
            p="0.4rem 0.2rem"
          >
            {allAliases.map((alias, index) => {
              return (
                <Alias
                  key={alias.key}
                  editMode={editMode}
                  name={alias.value}
                  index={index}
                  primaryNameIndex={primaryNameIndex}
                  setAllAliases={setAllAliases}
                  setPrimaryNameIndex={setPrimaryNameIndex}
                />
              );
            })}
          </Flex>
          {editMode && (
            <Flex justifyContent="flex-end" mt="0.5rem">
              <Button
                variant="link"
                colorScheme="blue"
                color="blue.400"
                leftIcon={<Icon icon="plus" />}
                size="sm"
                fontSize="sm"
                onClick={() => {
                  setAllAliases((prev) => [
                    ...prev,
                    { key: uuidv4(), value: '' },
                  ]);
                }}
              >
                Add Name
              </Button>
            </Flex>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};
